// RadarSectionPage — Slide-based story-telling view for Technology Radar sections
import { useState, useEffect, useCallback, useRef } from 'preact/hooks'
import { useLocation } from 'wouter-preact'
import { SECTION_BY_ID } from './radarConfig.js'
import LazyRadarPlot from '../visualizations/LazyRadarPlot.jsx'
import QAInsightBlock from './QAInsightBlock.jsx'
import SlideProgressBar from './SlideProgressBar.jsx'
import ZoomToggleButton from './ZoomToggleButton.jsx'
import DemoOverlay from './DemoOverlay.jsx'
import DemoCTA from './DemoCTA.jsx'
import { basis } from '../../state/globalSignals.js'
import { subscriptionTier } from '../../state/userState.js'
import { radarActivePlotIndex, radarActivePlotCount, radarExportAction } from '../../state/radarState.js'
import { getSlugFromProductId } from '../../utils/cellSlugMap.js'
import { getWatermarkGraphic } from '../../utils/chartWatermark.js'
import { exportSlideToPptx, exportSlideToPdf, exportSectionToPptx, extractText, getChartImagesFromInstances } from './radarExport.js'
import { RADAR_SECTIONS } from './radarConfig.js'
import { blockDemoExport } from '../../services/demoLimitService.js'
import { isCellLockedAtLimit, openLimitModalOrToast } from '../../state/demoLimitStore.js'

// Preload Batemo logo as PNG data URI for ECharts canvas export
let _logoPngDataUri = null
const _logoPromise = new Promise((resolve) => {
  const img = new Image()
  img.crossOrigin = 'anonymous'
  img.onload = () => {
    const canvas = document.createElement('canvas')
    canvas.width = img.naturalWidth
    canvas.height = img.naturalHeight
    const ctx = canvas.getContext('2d')
    ctx.drawImage(img, 0, 0)
    _logoPngDataUri = canvas.toDataURL('image/png')
    resolve(_logoPngDataUri)
  }
  img.onerror = () => resolve(null)
  img.src = '/logo/batemo_logo_white.svg'
})

// CSS
import '../../../css/components/radar/qa-insight.css'
import '../../../css/components/radar/slide-progress.css'
import '../../../css/components/radar/demo-overlay.css'

// Data fetching
import {
  fetchChemistryData,
  fetchPerformanceData,
  fetchNaionLfpData,
} from '../../services/radarService.js'

// Plot builders — Chemistry
import {
  buildRagoneByCathode,
  buildCathodeMarketShare,
  buildNmcEvolution,
  buildRagoneByAnode,
  buildAnodeMarketShare,
} from './plots/chemistryPlots.js'

// Plot builders — Performance
import {
  buildRagoneLowHighEnergy,
  buildEnergyDensityVsYear,
  buildPowerDensityVsYear,
  buildPerformanceEnvelope,
} from './plots/performancePlots.js'

// Plot builders — Na-Ion vs LFP
import {
  buildNaionLfpRagone,
  buildTemporalEvolution,
  buildKpiRadar,
} from './plots/naionLfpPlots.js'

// ===== Helpers =====

const J_TO_WH = 1 / 3600;

/**
 * Scale ECharts option text sizes and spacing for large export containers.
 * On-screen charts render at ~700px; export containers are 1200-1600px,
 * so text needs proportional scaling to remain readable in presentations.
 *
 * @param {Object} option - ECharts option from getOption() or buildOption()
 * @param {number} scale - Scale factor (e.g. 2.0 for 1600px export)
 * @returns {Object} New option with scaled text properties
 */
function scaleOptionForExport(option, scale) {
  const s = (v) => (typeof v === 'number' ? Math.round(v * scale) : v)

  const scaleTextStyle = (ts) => {
    if (!ts) return ts
    if (Array.isArray(ts)) return ts.map(scaleTextStyle)
    const out = { ...ts }
    if (out.fontSize) out.fontSize = s(out.fontSize)
    if (out.lineHeight) out.lineHeight = s(out.lineHeight)
    return out
  }

  const scaleAxis = (axis) => {
    if (!axis) return axis
    const arr = Array.isArray(axis) ? axis : [axis]
    return arr.map((a) => {
      const out = { ...a }
      if (out.nameTextStyle) out.nameTextStyle = scaleTextStyle(out.nameTextStyle)
      if (out.axisLabel) out.axisLabel = scaleTextStyle(out.axisLabel)
      if (out.nameGap) out.nameGap = s(out.nameGap)
      return out
    })
  }

  const result = { ...option }

  // Title
  if (result.title) {
    const titles = Array.isArray(result.title) ? result.title : [result.title]
    result.title = titles.map((t) => {
      const out = { ...t }
      if (out.textStyle) out.textStyle = scaleTextStyle(out.textStyle)
      if (out.subtextStyle) out.subtextStyle = scaleTextStyle(out.subtextStyle)
      if (typeof out.top === 'number') out.top = s(out.top)
      return out
    })
  }

  // Legend
  if (result.legend) {
    const legends = Array.isArray(result.legend) ? result.legend : [result.legend]
    result.legend = legends.map((l) => {
      const out = { ...l }
      if (out.textStyle) out.textStyle = scaleTextStyle(out.textStyle)
      if (out.itemWidth) out.itemWidth = s(out.itemWidth)
      if (out.itemHeight) out.itemHeight = s(out.itemHeight)
      if (out.itemGap) out.itemGap = s(out.itemGap)
      if (typeof out.top === 'number') out.top = s(out.top)
      return out
    })
  }

  // Axes
  result.xAxis = scaleAxis(result.xAxis)
  result.yAxis = scaleAxis(result.yAxis)

  // Grid
  if (result.grid) {
    const grids = Array.isArray(result.grid) ? result.grid : [result.grid]
    result.grid = grids.map((g) => {
      const out = { ...g }
      ;['left', 'right', 'top', 'bottom'].forEach((k) => {
        if (typeof out[k] === 'number') out[k] = s(out[k])
      })
      return out
    })
  }

  return result
}

// ===== Section → fetcher mapping =====

const SECTION_FETCHERS = {
  'chemistry-evolution': fetchChemistryData,
  'performance-limits': fetchPerformanceData,
  'naion-vs-lfp': fetchNaionLfpData,
}

// ===== 8-Slide Structure =====

const SECTION_SLIDES = {
  'chemistry-evolution': [
    {
      name: 'Cathode Chemistry',
      layout: 'dual',
      plots: [
        { name: 'Ragone by Cathode', buildOption: (data, b) => buildRagoneByCathode(data, b) },
        { name: 'Cathode Share', buildOption: (data) => buildCathodeMarketShare(data) },
      ],
      question: 'Cathode: The shift to high-nickel and LFP',
      insightFn: (data) => {
        const raw = data.market_share_cathode || {}
        const years = Object.keys(raw).sort()
        if (years.length < 2) return <p>Share data covers a single year.</p>
        const first = years[0]
        const last = years[years.length - 1]
        const norm = (yearData) => {
          const filtered = {}
          let total = 0
          Object.entries(yearData || {}).forEach(([g, v]) => {
            if (g !== 'analysis ongoing') { filtered[g] = v; total += v }
          })
          if (total === 0) return filtered
          const scale = 100 / total
          Object.keys(filtered).forEach((g) => { filtered[g] = filtered[g] * scale })
          return filtered
        }
        const firstN = norm(raw[first])
        const lastN = norm(raw[last])
        const ncaFirst = firstN['NCA'] || 0
        const ncaLast = lastN['NCA'] || 0
        const nmcHiLast = lastN['NMC (high-nickel)'] || 0
        const lfpLast = lastN['LFP'] || 0
        return (
          <p>
            The Ragone plot reveals distinct energy-power clusters per chemistry.
            Chemistry share confirms the trend:{' '}
            <strong>NCA dropped from {ncaFirst.toFixed(0)}% to {ncaLast.toFixed(0)}%</strong>{' '}
            ({first}–{last}), while <strong>NMC (high-nickel)</strong> with{' '}
            <strong>{nmcHiLast.toFixed(0)}%</strong> and <strong>LFP</strong> with{' '}
            <strong>{lfpLast.toFixed(0)}%</strong> get more dominant.
            NMC (low-nickel) tends to disappear due to its lower energy density,
            competing more and more with LFP.
          </p>
        )
      },
    },
    {
      name: 'NMC Nickel Evolution',
      layout: 'single',
      plots: [
        { name: 'NMC Evolution', buildOption: (data) => buildNmcEvolution(data) },
      ],
      question: 'NMC composition is shifting dramatically toward high-nickel',
      insightFn: (data) => {
        const raw = data.nmc_evolution || {}
        const years = Object.keys(raw).sort()
        if (years.length < 2) return <p>NMC evolution data is loading.</p>

        // Merge blends into base categories (same logic as buildNmcEvolution)
        const merged = {}
        years.forEach((y) => {
          merged[y] = {}
          Object.entries(raw[y] || {}).forEach(([key, value]) => {
            const base = key.replace(/\s*-?\s*[Bb]lend$/, '')
            merged[y][base] = (merged[y][base] || 0) + value
          })
        })

        // Re-normalize each year to 100%
        years.forEach((y) => {
          const total = Object.values(merged[y]).reduce((s, v) => s + v, 0)
          if (total > 0) {
            const scale = 100 / total
            Object.keys(merged[y]).forEach((k) => { merged[y][k] *= scale })
          }
        })

        // Detect high-nickel categories present in data
        const hiNiKeys = ['NMC811', 'NMC9505', 'NMC95']
        const allKeys = new Set()
        years.forEach((y) => Object.keys(merged[y]).forEach((k) => allKeys.add(k)))
        const activeHiNi = hiNiKeys.filter((k) => allKeys.has(k))

        const sumHiNi = (yearData) =>
          activeHiNi.reduce((s, k) => s + (yearData[k] || 0), 0)

        const first = years[0]
        const latest = years[years.length - 1]
        const hiNiFirst = sumHiNi(merged[first] || {})
        const hiNiLatest = sumHiNi(merged[latest] || {})

        // Find the newest high-nickel variant and when it first appeared
        const newestHiNi = [...activeHiNi].pop()
        let firstYear = null
        if (newestHiNi) {
          firstYear = years.find((y) => (merged[y][newestHiNi] || 0) > 0)
        }

        const hiNiLabel = activeHiNi.join(' + ')
        return (
          <p>
            High-Nickel ({hiNiLabel}) grew from <strong>{hiNiFirst.toFixed(0)}%</strong> in {first} to{' '}
            <strong>{hiNiLatest.toFixed(0)}%</strong> in {latest}.
            {newestHiNi && firstYear && firstYear !== first && (
              <>{' '}{newestHiNi} isn't visible before {firstYear} but then grew strongly, reflecting
              the industry's push toward higher energy density and lower cobalt content.</>
            )}
          </p>
        )
      },
    },
    {
      name: 'Anode Chemistry',
      layout: 'dual',
      plots: [
        { name: 'Ragone by Anode', buildOption: (data, b) => buildRagoneByAnode(data, b) },
        { name: 'Anode Share', buildOption: (data) => buildAnodeMarketShare(data) },
      ],
      question: 'Anode: silicon is established',
      insightFn: (data) => {
        const raw = data.market_share_anode || {}
        const years = Object.keys(raw).sort()
        if (years.length < 2) return <p>Anode share data is loading.</p>
        const latest = years[years.length - 1]
        const latestShare = raw[latest] || {}
        // Normalize to 100% excluding "analysis ongoing"
        const filtered = Object.entries(latestShare).filter(([k]) => k !== 'analysis ongoing')
        const total = filtered.reduce((s, [, v]) => s + v, 0)
        const norm = total > 0 ? 100 / total : 1
        const siShare =
          ((latestShare['graphite-silicon blend'] || 0) * norm)
        return (
          <p>
            The Ragone plot shows graphite-silicon blends achieving the highest energy densities.
            Chemistry share confirms: <strong>Si-blends at ~{siShare.toFixed(0)}%</strong> in {latest},
            coexisting with pure graphite for high-power and LFP cells.
            LTO remains niche for ultra-fast charge and high cycle life requirements.
          </p>
        )
      },
    },
  ],
  'performance-limits': [
    {
      name: 'Ragone Envelope',
      layout: 'single',
      plots: [
        { name: 'Ragone Low vs High Energy', buildOption: (data, b) => buildRagoneLowHighEnergy(data, b) },
      ],
      question: 'Clear separation between low-energy and high-energy chemistries',
      insightFn: (data) => {
        const lowCount = (data.ragone_low_energy || []).length
        const highCount = (data.ragone_high_energy || []).length
        return (
          <p>
            The Ragone plot maps <strong>{highCount}</strong> high-energy cells (NMC, NCA) against{' '}
            <strong>{lowCount}</strong> low-energy cells (LFP, Na-Ion, LTO). The convex hulls show
            the achievable energy-power envelope for each segment.
          </p>
        )
      },
    },
    {
      name: 'Temporal Trends',
      layout: 'dual',
      plots: [
        { name: 'Energy Density vs Year', buildOption: (data, b) => buildEnergyDensityVsYear(data, b) },
        { name: 'Power Density vs Year', buildOption: (data, b) => buildPowerDensityVsYear(data, b) },
      ],
      question: 'Both energy and power density improve year over year',
      insightFn: (data) => {
        const eBands = data.energy_density_bands || {}
        const pBands = data.power_density_bands || {}
        // Find the highest median energy and power for high-energy cells
        const hiEBands = eBands['High-Energy Chemistries'] || []
        const hiPBands = pBands['High-Energy Chemistries'] || []
        let bestE = null
        hiEBands.forEach((d) => {
          if (!bestE || d.median > bestE.median) bestE = d
        })
        let bestP = null
        hiPBands.forEach((d) => {
          if (!bestP || d.median > bestP.median) bestP = d
        })
        if (!bestE || !bestP) return <p>Loading trend data...</p>
        const hiE = (bestE.median * J_TO_WH).toFixed(0)
        const hiP = bestP.median.toFixed(0)
        return (
          <p>
            The median lines of high-energy cells peak at an energy density of{' '}
            <strong>{hiE} Wh/kg</strong> ({bestE.year}) and a peak power density of{' '}
            <strong>{hiP} W/kg</strong> ({bestP.year}).
            The P25–P75 bands show both segments steadily expanding their frontiers.
          </p>
        )
      },
    },
    {
      name: 'Performance Envelopes',
      layout: 'single-with-year-toggle',
      hasYearToggle: true,
      plots: [
        {
          name: 'Performance Envelopes',
          buildOption: (data, b, yearToggles) => buildPerformanceEnvelope(data, b, yearToggles),
        },
      ],
      question: 'Performance envelopes grow larger every year',
      insightFn: (data) => {
        const hullYears = Object.keys(data.hulls_by_year || {}).sort()
        if (hullYears.length === 0) return <p>Loading envelope data...</p>
        return (
          <p>
            Performance envelopes span{' '}
            <strong>
              {hullYears.length} years ({hullYears[0]}–{hullYears[hullYears.length - 1]})
            </strong>
            . Each successive envelope encapsulates a larger design space, illustrating how the
            achievable energy-power frontier has expanded.
          </p>
        )
      },
    },
  ],
  'naion-vs-lfp': [
    {
      name: 'Na-Ion vs LFP Ragone',
      layout: 'single-with-toggle',
      hasZoomToggle: true,
      plots: [
        {
          name: 'Na-Ion vs LFP Ragone',
          buildOption: (data, b, zoomState) => buildNaionLfpRagone(data, b, zoomState),
        },
      ],
      question: 'Na-Ion still trails LFP in energy density',
      insightFn: (data) => {
        const naionPts = (data.ragone_naion || []).filter((p) => p.e_grav != null)
        const lfpPts = (data.ragone_lfp || []).filter((p) => p.e_grav != null)
        const medNaion = naionPts.length
          ? naionPts.reduce((s, p) => s + p.e_grav, 0) / naionPts.length * J_TO_WH
          : 0
        const medLfp = lfpPts.length
          ? lfpPts.reduce((s, p) => s + p.e_grav, 0) / lfpPts.length * J_TO_WH
          : 0
        const gap = medLfp > 0 ? ((1 - medNaion / medLfp) * 100).toFixed(0) : '–'
        return (
          <p>
            Na-Ion averages <strong>{medNaion.toFixed(0)} Wh/kg</strong> vs LFP's{' '}
            <strong>{medLfp.toFixed(0)} Wh/kg</strong> — a <strong>{gap}%</strong> gap in
            gravimetric energy density. Material cost advantages may compensate at scale.
          </p>
        )
      },
    },
    {
      name: 'Evolution & KPI Profile',
      layout: 'dual',
      plots: [
        { name: 'Temporal Evolution', buildOption: (data) => buildTemporalEvolution(data) },
        { name: 'KPI Radar', buildOption: (data) => buildKpiRadar(data) },
      ],
      question: 'Na-Ion is converging toward LFP — and already competitive on key KPIs',
      insightFn: (data) => {
        const naion = data.temporal_naion || []
        const lfp = data.temporal_lfp || []
        const latestNaion = naion.length ? naion[naion.length - 1] : null
        const latestLfp = lfp.length ? lfp[lfp.length - 1] : null
        const naionKpi = data.kpi_naion || {}
        const lfpKpi = data.kpi_lfp || {}
        // Voltage and efficiency gaps (Na-Ion relative to LFP, as negative %)
        const vNaion = naionKpi.v_mean || 0
        const vLfp = lfpKpi.v_mean || 0
        const vGap = vLfp > 0 ? Math.abs(Math.round((1 - vNaion / vLfp) * 100)) : '–'
        const etaNaion = naionKpi.eta_dc_25 || 0
        const etaLfp = lfpKpi.eta_dc_25 || 0
        const etaGap = etaLfp > 0 ? Math.abs(Math.round((1 - etaNaion / etaLfp) * 100)) : '–'
        // Continuous power gap
        const pContNaion = naionKpi.p_cont_grav || 0
        const pContLfp = lfpKpi.p_cont_grav || 0
        const pContGap = pContLfp > 0 ? Math.abs(Math.round((1 - pContNaion / pContLfp) * 100)) : '–'
        if (!latestNaion || !latestLfp) return <p>Loading data...</p>
        const naionMed = (latestNaion.median * J_TO_WH).toFixed(0)
        const lfpMed = (latestLfp.median * J_TO_WH).toFixed(0)
        const year = latestLfp.year
        return (
          <>
            <p>
              Na-Ion at <strong>{naionMed} Wh/kg</strong> vs LFP at{' '}
              <strong>{lfpMed} Wh/kg</strong> ({year}) — the energy gap is narrowing.
              Voltage and efficiency are only slightly lower (<strong>–{vGap}%</strong> and{' '}
              <strong>–{etaGap}%</strong>), indicating that Na-Ion's electrochemical kinetics
              are already competitive.
            </p>
            <p>
              Continuous power is nearly equivalent (<strong>{pContGap}% less</strong> than
              LFP) — Na-Ion can deliver comparable power densities in real-world operation.
            </p>
          </>
        )
      },
    },
  ],
}

// ===== Chart Info Tooltip (? icon with explanation per chart) =====

const CHART_INFO = {
  'Ragone by Cathode':
    'Each dot is a battery cell, positioned by its peak power density (x) and energy density (y). ' +
    'Colors group cells by cathode chemistry. Convex hulls outline each group\'s performance envelope. ' +
    'Toggle between gravimetric (per kg) and volumetric (per liter) using the basis selector.',
  'Cathode Share':
    'Stacked bar chart showing the share of cathode chemistries per year. ' +
    'Each bar is normalized to 100%, so the height of a color band represents ' +
    'the percentage of cells using that chemistry in a given year.',
  'NMC Evolution':
    'Breakdown of NMC sub-types by nickel content. Blend variants are merged into their base category. ' +
    'Higher-nickel NMC (811, 95) offers better energy density but presents manufacturing challenges.',
  'Ragone by Anode':
    'Each dot is a battery cell, positioned by peak power (x) and energy density (y). ' +
    'Colors group cells by anode material. Convex hulls outline each group\'s performance envelope.',
  'Anode Share':
    'Stacked bar chart showing the share of anode materials per year, normalized to 100%. ' +
    'Silicon-graphite blends have been gaining share as manufacturers push for higher energy density.',
  'Ragone Low vs High Energy':
    'All cells split into two segments: high-energy (NMC, NCA) and low-energy (LFP, Na-Ion, LTO). ' +
    'Convex hulls show the achievable energy-power envelope for each segment.',
  'Energy Density vs Year':
    'Median energy density per year with P25\u2013P75 percentile bands. ' +
    'Individual cells are shown as small dots. The band width indicates the spread of the market in each year.',
  'Power Density vs Year':
    'Median peak power density per year with P25\u2013P75 percentile bands. ' +
    'Individual cells are shown as small dots. The band width indicates the spread of the market in each year.',
  'Performance Envelopes':
    'Cumulative convex hulls showing how the performance frontier expands over time. ' +
    'Use the year buttons to toggle which envelopes are visible. ' +
    'Each hull includes all cells up to and including that year.',
  'Na-Ion vs LFP Ragone':
    'Na-Ion (diamonds) and LFP (circles) cells highlighted against all other cells (gray). ' +
    'Convex hulls outline each chemistry\'s performance range. Use the zoom toggle to focus on the Na-Ion/LFP region.',
  'Temporal Evolution':
    'Energy density trend over time for Na-Ion and LFP. Shaded bands show P25\u2013P75 range, ' +
    'the line shows the median. Individual cells are overlaid as small dots.',
  'KPI Radar':
    'Each axis shows a normalized score from 0 to 100, based on the group median. ' +
    '100 corresponds to the best-performing cell and 0 to the worst-performing cell ' +
    'across the entire database (all chemistries, not just Na-Ion and LFP). ' +
    'Multiple years are overlaid with increasing opacity \u2014 the most opaque shape is the latest year. ' +
    'Yearly values are cumulative: e.g. "LFP 2025" includes all LFP cells up to and including 2025.',
}

function ChartInfo({ plotName }) {
  const text = CHART_INFO[plotName]
  if (!text) return null
  const [open, setOpen] = useState(false)
  return (
    <div className="chart-info">
      <button
        className="chart-info__btn"
        onClick={() => setOpen(!open)}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        title="How to read this chart"
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10" />
          <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
          <line x1="12" y1="17" x2="12.01" y2="17" />
        </svg>
      </button>
      {open && (
        <div className="chart-info__tooltip">
          <strong>How to read this chart</strong>
          <p>{text}</p>
        </div>
      )}
    </div>
  )
}

// ===== Year Toggle (Performance Envelope) =====

function YearToggle({ yearToggles, onChange }) {
  return (
    <div className="radar-year-toggle">
      {[2021, 2023, 2025].map((year) => (
        <button
          key={year}
          className={`radar-year-toggle__btn ${yearToggles[year] ? 'radar-year-toggle__btn--active' : ''}`}
          onClick={() => onChange({ ...yearToggles, [year]: !yearToggles[year] })}
        >
          {year}
        </button>
      ))}
    </div>
  )
}

// ===== Main Component =====

export default function RadarSectionPage({ sectionId }) {
  const section = SECTION_BY_ID[sectionId]
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [yearToggles, setYearToggles] = useState({ 2021: true, 2023: true, 2025: true })
  const [zoomState, setZoomState] = useState('all')
  const [slideDirection, setSlideDirection] = useState(1) // 1=forward, -1=backward
  const [pptxExporting, setPptxExporting] = useState(false)
  const [, navigate] = useLocation()
  const chartInstancesRef = useRef([])

  const activePlotIndex = radarActivePlotIndex.value
  const currentBasis = basis.value
  const isDemo = subscriptionTier.value === 'DEMO'
  const slides = SECTION_SLIDES[sectionId] || []

  // Reset state when section changes — read slide from URL if present (URL is 1-based)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const slideParam = parseInt(params.get('slide'), 10)
    const slideCount = (SECTION_SLIDES[sectionId] || []).length
    if (!isNaN(slideParam) && slideParam >= 1 && slideParam <= slideCount) {
      radarActivePlotIndex.value = slideParam - 1
    } else {
      radarActivePlotIndex.value = 0
    }
    setZoomState('all')
  }, [sectionId])

  // Sync slide index to URL (permalink support, 1-based)
  useEffect(() => {
    const url = new URL(window.location)
    url.searchParams.set('slide', String(radarActivePlotIndex.value + 1))
    window.history.replaceState({}, '', url)
  }, [activePlotIndex])

  // Set slide count for Context Bar navigation
  useEffect(() => {
    radarActivePlotCount.value = slides.length
  }, [sectionId, slides.length])

  // Section navigation helpers
  const sectionIdx = RADAR_SECTIONS.findIndex((s) => s.id === sectionId)
  const nextSection = sectionIdx < RADAR_SECTIONS.length - 1 ? RADAR_SECTIONS[sectionIdx + 1] : null
  const prevSection = sectionIdx > 0 ? RADAR_SECTIONS[sectionIdx - 1] : null
  const isLastSlide = activePlotIndex >= slides.length - 1
  const isFirstSlide = activePlotIndex === 0

  // Keyboard navigation — crosses section boundaries
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft' && radarActivePlotIndex.value > 0) {
        setSlideDirection(-1)
        radarActivePlotIndex.value = radarActivePlotIndex.value - 1
      } else if (e.key === 'ArrowLeft' && radarActivePlotIndex.value === 0 && prevSection) {
        navigate(prevSection.route)
      } else if (e.key === 'ArrowRight' && radarActivePlotIndex.value < slides.length - 1) {
        setSlideDirection(1)
        radarActivePlotIndex.value = radarActivePlotIndex.value + 1
      } else if (e.key === 'ArrowRight' && radarActivePlotIndex.value >= slides.length - 1 && nextSection) {
        navigate(nextSection.route)
      } else if (e.key === 'ArrowRight' && radarActivePlotIndex.value >= slides.length - 1 && !nextSection) {
        navigate('/radar')
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [sectionId, slides.length])

  // Data fetching
  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    setData(null)

    const fetcher = SECTION_FETCHERS[sectionId]
    if (!fetcher) {
      setLoading(false)
      return
    }

    fetcher()
      .then((result) => {
        if (!cancelled) {
          setData(result)
          setLoading(false)
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err.message)
          setLoading(false)
        }
      })

    return () => { cancelled = true }
  }, [sectionId])

  // --- Not found ---
  if (!section) {
    return (
      <div className="radar-section radar-section--not-found">
        <h2>Section not found</h2>
        <p>The section "{sectionId}" does not exist.</p>
      </div>
    )
  }

  // --- Loading skeleton ---
  if (loading) {
    return (
      <div className="radar-section radar-section--viewport">
        <SlideProgressBar current={0} total={slides.length} accentColor={section.accentColor} />
        <div className="radar-slide">
          <div className="radar-slide__plots radar-slide__plots--single">
            <div className="radar-plot-container">
              <div className="chart-skeleton" style={{ height: '100%' }} />
            </div>
          </div>
          <div className="radar-slide__insight">
            <QAInsightBlock
              question="Loading..."
              answer={<p>Loading data...</p>}
              accentColor={section.accentColor}
              slideKey={-1}
            />
          </div>
        </div>
      </div>
    )
  }

  // --- Error state ---
  if (error) {
    return (
      <div className="radar-section radar-section--viewport">
        <div className="radar-section__error">
          <p>Failed to load data: {error}</p>
          <button
            className="radar-section__retry-btn"
            onClick={() => {
              setError(null)
              setLoading(true)
              const fetcher = SECTION_FETCHERS[sectionId]
              if (fetcher) {
                fetcher()
                  .then((result) => { setData(result); setLoading(false) })
                  .catch((err) => { setError(err.message); setLoading(false) })
              }
            }}
          >
            Retry
          </button>
        </div>
      </div>
    )
  }

  // --- Render active slide ---
  const slide = slides[activePlotIndex]
  if (!slide) return null

  // Reset chart instances on slide change
  useEffect(() => {
    chartInstancesRef.current = []
  }, [activePlotIndex, sectionId])

  // Factory: creates a chart-ready handler that stores the instance at the correct index
  const makeChartReadyHandler = (plotIndex) => (chartInstance) => {
    // Store at correct position to preserve plot order
    chartInstancesRef.current[plotIndex] = chartInstance
    handleChartInit(chartInstance)
  }

  // Click-through + export setup for each chart instance
  const handleChartInit = useCallback((chartInstance) => {

    chartInstance.on('click', (params) => {
      const productId = params.data?._meta?.product_id
      if (!productId) return
      // Bug B: DEMO users at today's deep-dive limit — clicking through to a
      // cell not yet opened today opens the limit modal/toast instead of
      // navigating (mirrors GridView/TableView/CompareCellChip guards).
      if (isCellLockedAtLimit(productId)) {
        openLimitModalOrToast({})
        return
      }
      const slug = getSlugFromProductId(productId)
      if (slug) {
        navigate(`/explorer?cell=${slug}&section=factsheet`)
      }
    })
    // Show pointer cursor on scatter points with product_id
    chartInstance.on('mouseover', (params) => {
      if (params.data?._meta?.product_id) {
        chartInstance.getZr().setCursorStyle('pointer')
      }
    })
    chartInstance.on('mouseout', () => {
      chartInstance.getZr().setCursorStyle('default')
    })

    // Export function: renders chart in hidden 16:9 container for consistent aspect ratio
    chartInstance._batemoExport = async (exportName) => {
      await _logoPromise
      const echarts = await import('echarts')

      // Fixed 4:3 export container (1600x1200) for consistent aspect ratio
      const div = document.createElement('div')
      div.style.cssText = 'position:absolute;left:-9999px;width:1600px;height:1200px;'
      document.body.appendChild(div)

      const exportChart = echarts.init(div, null, { renderer: 'canvas' })
      const currentOption = chartInstance.getOption()
      const scaledOption = scaleOptionForExport(currentOption, 2.0)
      exportChart.setOption({ ...scaledOption, animation: false })

      // Add branding (scaled for export)
      const brandingGraphic = [
        {
          type: 'text', id: '_brandUrl', left: 24, bottom: 28,
          style: { text: 'insights.batemo.com', fill: 'rgba(255,255,255,0.25)', font: '18px Roboto, sans-serif' },
          silent: true,
        },
      ]
      if (_logoPngDataUri) {
        brandingGraphic.push({
          type: 'image', id: '_brandLogo', right: 20, bottom: 18,
          style: { image: _logoPngDataUri, width: 180, height: 47, opacity: 0.3 },
          silent: true,
        })
      } else {
        brandingGraphic.push({
          type: 'text', id: '_brandLogo', right: 24, bottom: 28,
          style: { text: 'BATEMO', fill: 'rgba(255,255,255,0.25)', font: 'bold 22px "Big John", sans-serif' },
          silent: true,
        })
      }
      exportChart.setOption({ graphic: brandingGraphic })

      const url = exportChart.getDataURL({
        type: 'png', pixelRatio: 2, backgroundColor: '#272727', excludeComponents: ['toolbox'],
      })

      exportChart.dispose()
      document.body.removeChild(div)

      const a = document.createElement('a')
      a.href = url
      a.download = `${exportName}.png`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
    }
  }, [navigate])

  // Build plot options — injects export toolbox + click-through into every chart
  const renderPlot = (plotDesc, key) => {
    let option
    if (slide.hasYearToggle) {
      option = plotDesc.buildOption(data, currentBasis, yearToggles)
    } else if (slide.hasZoomToggle) {
      option = plotDesc.buildOption(data, currentBasis, zoomState)
    } else {
      option = plotDesc.buildOption(data, currentBasis)
    }

    // Add watermark
    option.graphic = [...(option.graphic || []), getWatermarkGraphic({
      position: 'top-right',
      opacity: 0.12,
      grid: { left: 90, right: 30, top: 64, bottom: 60 }
    })]

    const useNotMerge = slide.hasYearToggle || slide.hasZoomToggle
    const exportName = `batemo-${plotDesc.name.toLowerCase().replace(/\s+/g, '-')}`

    const handleChartPngExport = () => {
      // Demo users explore freely, but exporting is the PRO take-away: open the
      // upsell + record the signal instead of downloading.
      if (blockDemoExport('radar')) return
      const inst = chartInstancesRef.current[key]
      if (inst && inst._batemoExport) inst._batemoExport(exportName)
    }

    return (
      <div className="radar-plot-container" key={key} {...(key === 0 ? { 'data-tour': 'radar-plot-container' } : {})}>
        <LazyRadarPlot
          option={option}
          height="100%"
          notMerge={useNotMerge}
          onChartReady={makeChartReadyHandler(key)}
        />
        {isDemo && <div className="radar-demo-scrim" aria-hidden="true" />}
        {isDemo && <DemoCTA />}
        <ChartInfo plotName={plotDesc.name} />
        <button
          className="radar-chart-download-btn"
          onClick={handleChartPngExport}
          title="Save as PNG"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="7 10 12 15 17 10" />
            <line x1="12" y1="15" x2="12" y2="3" />
          </svg>
        </button>
      </div>
    )
  }

  const layout = slide.layout || 'single'
  const plots = slide.plots || []

  // Build controls (only for non-overlay layouts)
  let controls = null
  if (slide.hasYearToggle && slide.layout !== 'single-with-year-toggle') {
    controls = <YearToggle yearToggles={yearToggles} onChange={setYearToggles} />
  }

  // Compute answer — demo users see a full-population disclaimer instead of
  // quantitative insights (Bug E, Decision 6: aggregates are full-population
  // now, so this must never imply a demo subset — no more "based on your
  // demo cell selection").
  const answer = isDemo
    ? (
      <p>
        <em>
          Trends and statistics are computed across the full measured catalogue.
          {' '}Detailed per-cell metrics are part of PRO.
        </em>
      </p>
    )
    : (slide.insightFn ? slide.insightFn(data) : <p>Insight for {slide.name}.</p>)

  // PowerPoint export handler
  const handlePptxExport = useCallback(async () => {
    if (pptxExporting || chartInstancesRef.current.length === 0) return
    setPptxExporting(true)
    try {
      const slideName = slide.name.toLowerCase().replace(/\s+/g, '-')
      await exportSlideToPptx({
        chartInstances: chartInstancesRef.current.filter(Boolean),
        slideTitle: slide.question,
        insightJsx: answer,
        sectionTitle: section.title,
        fileName: `batemo-${sectionId}-${slideName}`,
      })
    } catch (err) {
      console.error('[Radar] PPTX export failed:', err)
    } finally {
      setPptxExporting(false)
    }
  }, [slide, answer, section, sectionId, pptxExporting])

  // PDF export handler
  const [pdfExporting, setPdfExporting] = useState(false)
  const handlePdfExport = useCallback(async () => {
    if (pdfExporting || chartInstancesRef.current.length === 0) return
    setPdfExporting(true)
    try {
      const slideName = slide.name.toLowerCase().replace(/\s+/g, '-')
      await exportSlideToPdf({
        chartInstances: chartInstancesRef.current.filter(Boolean),
        slideTitle: slide.question,
        insightJsx: answer,
        sectionTitle: section.title,
        fileName: `batemo-${sectionId}-${slideName}`,
      })
    } catch (err) {
      console.error('[Radar] PDF export failed:', err)
    } finally {
      setPdfExporting(false)
    }
  }, [slide, answer, section, sectionId, pdfExporting])

  // Full section PPTX export — renders all slides' charts in hidden containers
  const [sectionExporting, setSectionExporting] = useState(false)
  const handleSectionExport = useCallback(async () => {
    if (sectionExporting || !data) return
    setSectionExporting(true)
    try {
      const echarts = await import('echarts')
      const allSlides = SECTION_SLIDES[sectionId] || []
      const slideDataArray = []

      for (const s of allSlides) {
        // Build chart options for each plot on this slide
        const chartImages = []
        for (const plotDesc of s.plots) {
          const option = plotDesc.buildOption(data, currentBasis)

          // Create hidden container and render chart
          const div = document.createElement('div')
          div.style.cssText = 'position:absolute;left:-9999px;width:1200px;height:700px;'
          document.body.appendChild(div)

          const chart = echarts.init(div, null, { renderer: 'canvas' })
          // Disable animation so chart renders immediately for getDataURL
          const scaledOpt = scaleOptionForExport(option, 1.6)
          chart.setOption({ ...scaledOpt, animation: false })

          // Add branding watermark — real logo if available (scaled for export)
          await _logoPromise
          const deckBranding = [
            {
              type: 'text',
              id: '_deckBrandUrl',
              left: 20,
              bottom: 24,
              style: {
                text: 'insights.batemo.com',
                fill: 'rgba(255,255,255,0.25)',
                font: '16px Roboto, sans-serif',
              },
              silent: true,
            },
          ]
          if (_logoPngDataUri) {
            deckBranding.push({
              type: 'image',
              id: '_deckBrandLogo',
              right: 16,
              bottom: 16,
              style: {
                image: _logoPngDataUri,
                width: 160,
                height: 42,
                opacity: 0.3,
              },
              silent: true,
            })
          }
          chart.setOption({ graphic: deckBranding })

          const img = chart.getDataURL({
            type: 'png',
            pixelRatio: 3,
            backgroundColor: '#272727',
            excludeComponents: ['toolbox'],
          })
          chartImages.push(img)

          chart.dispose()
          document.body.removeChild(div)
        }

        // Compute insight text
        const insightJsx = s.insightFn ? s.insightFn(data) : null
        slideDataArray.push({
          slideTitle: s.question,
          insightText: extractText(insightJsx),
          chartImages,
        })
      }

      await exportSectionToPptx({
        sectionTitle: section.title,
        sectionDescription: section.description || '',
        accentColor: (section.accentColor || '#ea5b0c').replace('#', ''),
        slides: slideDataArray,
        fileName: `batemo-technology-radar-${sectionId}`,
      })
    } catch (err) {
      console.error('[Radar] Section PPTX export failed:', err)
    } finally {
      setSectionExporting(false)
    }
  }, [data, sectionId, section, currentBasis, sectionExporting])

  // PNG export handler — exports all charts on current slide with branding
  const handlePngExport = useCallback(() => {
    const instances = chartInstancesRef.current
    if (!instances.length) return
    const slideName = slide.name.toLowerCase().replace(/\s+/g, '-')
    instances.forEach((inst, i) => {
      if (inst && inst._batemoExport) {
        const suffix = instances.length > 1 ? `-${i + 1}` : ''
        inst._batemoExport(`batemo-${sectionId}-${slideName}${suffix}`)
      }
    })
  }, [slide, sectionId])

  // Listen for export actions from context bar dropdown
  useEffect(() => {
    const action = radarExportAction.value
    if (!action) return
    radarExportAction.value = null // consume the action

    if (action === 'pptx-slide') handlePptxExport()
    else if (action === 'pptx-section') handleSectionExport()
  }, [radarExportAction.value])

  return (
    <div
      className="radar-section radar-section--viewport"
      data-tour="radar-section-page"
      style={{ '--slide-direction': slideDirection > 0 ? '30px' : '-30px' }}
    >
      <SlideProgressBar
        current={activePlotIndex}
        total={slides.length}
        accentColor={section.accentColor}
      />

      <div className="radar-slide radar-slide--enter" key={`${sectionId}-${activePlotIndex}`}>
        {controls && <div className="radar-slide__controls">{controls}</div>}

        {/* Bug E, Task 1 (+ review fix): rendered once per slide (not once per
            plot) — dual-layout slides previously mounted DemoOverlay twice
            (once per renderPlot call). It now lives inside the
            `.radar-slide__plots` wrapper (not at `.radar-slide` level) so its
            `position:absolute; bottom:0` anchors to the plots area only —
            not the whole slide, which would overlap the insight text/buttons
            below. `.radar-slide__plots` is `position:relative` for this. */}

        {layout === 'single' && (
          <div className="radar-slide__plots radar-slide__plots--single">
            {plots[0] && renderPlot(plots[0], 0)}
            {isDemo && <DemoOverlay />}
          </div>
        )}

        {layout === 'dual' && (
          <div className="radar-slide__plots radar-slide__plots--dual">
            {plots.map((p, i) => renderPlot(p, i))}
            {isDemo && <DemoOverlay />}
          </div>
        )}

        {layout === 'single-with-toggle' && (
          <div className="radar-slide__plots radar-slide__plots--single-with-toggle">
            <div className="radar-slide__toggle-overlay">
              <ZoomToggleButton zoomState={zoomState} onChange={setZoomState} />
            </div>
            {plots[0] && renderPlot(plots[0], 0)}
            {isDemo && <DemoOverlay />}
          </div>
        )}

        {layout === 'single-with-year-toggle' && (
          <div className="radar-slide__plots radar-slide__plots--single-with-toggle">
            <div className="radar-slide__toggle-overlay">
              <YearToggle yearToggles={yearToggles} onChange={setYearToggles} />
            </div>
            {plots[0] && renderPlot(plots[0], 0)}
            {isDemo && <DemoOverlay />}
          </div>
        )}

        {layout === 'complex' && (
          <div className="radar-slide__plots radar-slide__plots--complex">
            <div className="radar-slide__plot-main radar-plot-container">
              {plots[0] && (
                <LazyRadarPlot
                  option={plots[0].buildOption(data, currentBasis)}
                  height="100%"
                />
              )}
              {isDemo && <div className="radar-demo-scrim" aria-hidden="true" />}
              {isDemo && <DemoCTA />}
            </div>
            <div className="radar-slide__plot-stack">
              {plots.slice(1).map((p, i) => renderPlot(p, i + 1))}
            </div>
            {isDemo && <DemoOverlay />}
          </div>
        )}

        <div className="radar-slide__insight" data-tour="radar-key-insight">
          <QAInsightBlock
            question={slide.question}
            answer={answer}
            accentColor={section.accentColor}
            slideKey={activePlotIndex}
          />
          {isLastSlide && (
            <div className="radar-next-section-row">
              {nextSection ? (
                <button
                  className="radar-next-section-btn"
                  onClick={() => navigate(nextSection.route)}
                  style={{ '--next-accent': nextSection.accentColor }}
                >
                  Next: {nextSection.title}
                  <svg viewBox="0 0 24 24" width="16" height="16"><polyline points="9 18 15 12 9 6" /></svg>
                </button>
              ) : (
                <button
                  className="radar-next-section-btn"
                  onClick={() => navigate('/radar')}
                >
                  Back to Radar Overview
                  <svg viewBox="0 0 24 24" width="16" height="16"><polyline points="9 18 15 12 9 6" /></svg>
                </button>
              )}
              {nextSection && (
                <button
                  className="radar-back-overview-link"
                  onClick={() => navigate('/radar')}
                >
                  or back to overview
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
